// Date utility functions for Spirit Research Lab
import { isValid, format } from "date-fns";

const MONTH_MAP: Record<string, number> = {
  // English full
  january: 1, february: 2, march: 3, april: 4, may: 5, june: 6,
  july: 7, august: 8, september: 9, october: 10, november: 11, december: 12,
  // English short
  jan: 1, feb: 2, mar: 3, apr: 4, jun: 6, jul: 7, aug: 8, sep: 9, oct: 10, nov: 11, dec: 12,
  
  // Hebrew full & with ב prefix
  ינואר: 1, בינואר: 1,
  פברואר: 2, בפברואר: 2,
  מרץ: 3, במרץ: 3, מרס: 3, במרס: 3, מארץ: 3, במארץ: 3,
  אפריל: 4, באפריל: 4,
  מאי: 5, במאי: 5,
  יוני: 6, ביוני: 6,
  יולי: 7, ביולי: 7,
  אוגוסט: 8, באוגוסט: 8,
  ספטמבר: 9, בספטמבר: 9,
  אוקטובר: 10, באוקטובר: 10,
  נובמבר: 11, בנובמבר: 11,
  דצמבר: 12, בדצמבר: 12,

  // Hebrew abbreviated & with ב prefix
  ינו: 1, בינו: 1,
  פבר: 2, בפבר: 2,
  אפר: 4, באפר: 4,
  יונ: 6, ביונ: 6,
  יול: 7, ביול: 7,
  אוג: 8, באוג: 8,
  ספט: 9, בספט: 9,
  אוק: 10, באוק: 10,
  נוב: 11, בנוב: 11,
  דצמ: 12, בדצמ: 12
};

const getMonthFromToken = (token: string): number | null => {
  const lowerToken = token.toLowerCase();
  if (MONTH_MAP[lowerToken] !== undefined) {
    return MONTH_MAP[lowerToken];
  }
  const cleanToken = lowerToken.replace(/['"ב]/g, '');
  if (MONTH_MAP[cleanToken] !== undefined) {
    return MONTH_MAP[cleanToken];
  }
  if (lowerToken.startsWith('ב') && lowerToken.length > 1) {
    const stripped = lowerToken.substring(1);
    if (MONTH_MAP[stripped] !== undefined) {
      return MONTH_MAP[stripped];
    }
  }
  return null;
};

const isLeapYear = (year: number): boolean => {
  return (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
};

const isValidDateParts = (day: number, month: number, year: number): boolean => {
  if (month < 1 || month > 12) return false;
  if (day < 1 || day > 31) return false;
  if (year < 1) return false;

  const daysInMonth = [31, isLeapYear(year) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  return day <= daysInMonth[month - 1];
};

const expandYear = (yearNum: number): number => {
  if (yearNum >= 100) return yearNum;
  const currentYear = new Date().getFullYear();
  const cutoff = (currentYear + 10) % 100;
  return yearNum <= cutoff ? 2000 + yearNum : 1900 + yearNum;
};

/**
 * Standardizes any input date string (including Hebrew/English month names, varying spaces/delimiters, 
 * and 2-digit cutoff years) to "dd/mm/yyyy" format.
 * Returns null if parsing fails.
 */
export const cleanToDisplayDate = (dateStr: string | null | undefined): string | null => {
  if (!dateStr || typeof dateStr !== "string") return null;
  const trimmed = dateStr.trim();
  if (trimmed === "") return null;

  // 1. Preprocess: replace keyboard counterparts (q, ת, ץ) with '/' when they appear between digits
  const preprocessed = trimmed.replace(/(\d)\s*[qתץ]\s*(\d)/gi, '$1/$2');

  // 2. Check if it matches ISO format yyyy-MM-dd
  if (/^\d{4}-\d{2}-\d{2}$/.test(preprocessed)) {
    const [y, m, d] = preprocessed.split("-").map(Number);
    if (isValidDateParts(d, m, y)) {
      return `${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')}/${y}`;
    }
    return null;
  }

  // 3. Tokenize by slashes, dashes, dots, commas, and whitespace
  const tokens = preprocessed.split(/[/\-.\s,]+/).map(t => t.trim()).filter(Boolean);

  if (tokens.length === 1) {
    const tok = tokens[0];
    if (/^\d{8}$/.test(tok)) {
      // DDMMYYYY
      const d = parseInt(tok.substring(0, 2), 10);
      const m = parseInt(tok.substring(2, 4), 10);
      const y = parseInt(tok.substring(4, 8), 10);
      if (isValidDateParts(d, m, y)) {
        return `${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')}/${y}`;
      }
    } else if (/^\d{6}$/.test(tok)) {
      // DDMMYY
      const d = parseInt(tok.substring(0, 2), 10);
      const m = parseInt(tok.substring(2, 4), 10);
      const y2 = parseInt(tok.substring(4, 6), 10);
      const y = expandYear(y2);
      if (isValidDateParts(d, m, y)) {
        return `${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')}/${y}`;
      }
    }
    return null;
  }

  if (tokens.length === 3) {
    let dayStr = "";
    let monthVal = 0;
    let yearStr = "";

    const monthIndex = tokens.findIndex(t => getMonthFromToken(t) !== null);

    if (monthIndex !== -1) {
      monthVal = getMonthFromToken(tokens[monthIndex])!;
      if (monthIndex === 1) {
        // Day Month Year (e.g. "20 Oct 19" or "20 באוקטובר 2019")
        dayStr = tokens[0];
        yearStr = tokens[2];
      } else if (monthIndex === 0) {
        // Month Day Year (e.g. "Oct 20 2019")
        dayStr = tokens[1];
        yearStr = tokens[2];
      } else {
        // Month at the end (rare)
        dayStr = tokens[0];
        yearStr = tokens[1];
      }
    } else {
      // All tokens are numeric
      if (/^\d{4}$/.test(tokens[0])) {
        // YYYY-MM-DD format with custom separator
        yearStr = tokens[0];
        monthVal = parseInt(tokens[1], 10);
        dayStr = tokens[2];
      } else {
        // Default to DD/MM/YYYY
        dayStr = tokens[0];
        monthVal = parseInt(tokens[1], 10);
        yearStr = tokens[2];
      }
    }

    const d = parseInt(dayStr, 10);
    const yRaw = parseInt(yearStr, 10);
    const y = expandYear(yRaw);

    if (!isNaN(d) && !isNaN(monthVal) && !isNaN(y)) {
      if (isValidDateParts(d, monthVal, y)) {
        return `${String(d).padStart(2, '0')}/${String(monthVal).padStart(2, '0')}/${y}`;
      }
    }
  }

  return null;
};

/**
 * Parses a date string into a standardized format (dd/mm/yyyy)
 * Returns null if parsing fails
 */
export const parseDateString = (dateStr: string): string | null => {
  return cleanToDisplayDate(dateStr);
};

/**
 * Safely parses a date string into a Date object
 */
export const parseToDate = (dateStr: string | null | undefined): Date | undefined => {
  const clean = cleanToDisplayDate(dateStr);
  if (!clean) return undefined;
  const [d, m, y] = clean.split("/").map(Number);
  const parsedDate = new Date(y, m - 1, d);
  return isValid(parsedDate) ? parsedDate : undefined;
};

/**
 * Formats a date string safely, returning a fallback if invalid
 */
export const safeFormat = (dateStr: string | null | undefined, formatStr: string, fallback: string = "-"): string => {
  const date = parseToDate(dateStr);
  if (!date) return fallback;
  try {
    return format(date, formatStr);
  } catch (error) {
    console.error("Error formatting date:", error);
    return fallback;
  }
};

/**
 * Formats a date string (YYYY-MM-DD) for display (DD/MM/YYYY)
 */
export const formatForDisplay = (
  dateStr: string | null | undefined,
): string => {
  if (!dateStr) return "";
  // If it's already in the target format or similar, try to parse and reformat
  const parsed = parseDateString(dateStr);
  return parsed || "";
};

/**
 * Formats a display date string (DD/MM/YYYY) for storage (YYYY-MM-DD)
 * Returns null if invalid
 */
export const formatForStorage = (dateStr: string): string | null => {
  const clean = cleanToDisplayDate(dateStr);
  if (!clean) return null;
  const [d, m, y] = clean.split("/");
  return `${y}-${m}-${d}`;
};

/**
 * Checks if a date string is valid according to supported formats
 */
export const isValidDate = (dateStr: string): boolean => {
  return parseDateString(dateStr) !== null;
};

// Helper to get Israel timezone offset for a specific date (handling Summer/Winter time)
export const getIsraelOffset = (date: Date): string => {
  const offset = getTimezoneOffset("Asia/Jerusalem", date);
  return `utc${offset}`;
};

/**
 * Gets the UTC offset for a given IANA timezone and date.
 * Returns a string like "+02:00" or "-05:00"
 */
export const getTimezoneOffset = (timeZone: string, date: Date = new Date()): string => {
  try {
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone,
      timeZoneName: "shortOffset",
    });
    const parts = formatter.formatToParts(date);
    const offsetPart = parts.find((part) => part.type === "timeZoneName");

    if (!offsetPart) return "+00:00";

    // offsetPart.value is like "GMT+2" or "GMT-05:00" or "UTC+2"
    let offset = offsetPart.value.replace("GMT", "").replace("UTC", "");
    if (offset === "" || offset === "Z" || !/[+-]\d/.test(offset)) return "+00:00";

    // Standardize to +/-HH:mm
    if (!offset.includes(":")) {
      const isNegative = offset.startsWith("-");
      const absoluteValue = offset.replace(/[+-]/, "");
      const hours = absoluteValue.padStart(2, '0');
      offset = `${isNegative ? '-' : '+'}${hours}:00`;
    } else {
      // Ensure leading sign and padding
      const isNegative = offset.startsWith("-");
      const offsetBody = offset.replace(/[+-]/, "");
      const parts = offsetBody.split(":");
      const hours = (parts[0] || "00").padStart(2, '0');
      const minutes = (parts[1] || "00").padStart(2, '0');
      offset = `${isNegative ? '-' : '+'}${hours}:${minutes}`;
    }

    return offset;
  } catch (error) {
    console.error("Error getting timezone offset:", error);
    return "+00:00";
  }
};

// Generate birth map URL for Swiss Ephemeris
export const generateBirthMapUrl = (birthDate: string): string => {
  try {
    const standardizedDate = parseDateString(birthDate);
    if (!standardizedDate) {
      return "https://www.myhora.com/ephemeris/";
    }

    const [_day, month, year] = standardizedDate.split("/");
    const monthNum = parseInt(month);

    // Create date object for timezone calculation (using noon to avoid edge cases)
    const dateObj = new Date(
      parseInt(year),
      monthNum - 1,
      parseInt(_day),
      12,
      0,
      0,
    );
    const tzQuery = getIsraelOffset(dateObj);

    const monthNames = [
      "january",
      "february",
      "march",
      "april",
      "may",
      "june",
      "july",
      "august",
      "september",
      "october",
      "november",
      "december",
    ];

    const monthName = monthNames[monthNum - 1];
    return `https://www.myhora.com/ephemeris/${monthName}-${year}.aspx?tz=${tzQuery}`;
  } catch (error) {
    console.error("Error generating birth map URL:", error);
    return "https://www.myhora.com/ephemeris/";
  }
};

// Get month name in Hebrew for display
export const getHebrewMonthName = (birthDate: string): string => {
  try {
    const standardizedDate = parseDateString(birthDate);
    if (!standardizedDate) return "חודש לא ידוע";

    const [_day, month, _year] = standardizedDate.split("/");
    const monthNum = parseInt(month);

    const hebrewMonths = [
      "ינואר",
      "פברואר",
      "מרץ",
      "אפריל",
      "מאי",
      "יוני",
      "יולי",
      "אוגוסט",
      "ספטמבר",
      "אוקטובר",
      "נובמבר",
      "דצמבר",
    ];

    return hebrewMonths[monthNum - 1] || "חודש לא ידוע";
  } catch (_error) {
    return "חודש לא ידוע";
  }
};
